import React, { useState, useEffect, useMemo } from 'react'
import { Spin, Typography } from 'antd'
import { ClipboardList, Users } from 'lucide-react'
import { SurveyService } from '../../features/survey/services'
import { QuestionInsightCard } from '../../features/survey/components/insights'
import { reconcileQuestions } from '../../features/survey/pages/surveyInsightsUtils'
import type { SurveyResponsesResponse } from '@bandos/shared'
import { TYPOGRAPHY } from '../../styles/theme'

const { Text } = Typography

interface SurveyInsightsSectionProps {
  nodeId: string
  onNodeClick?: (e: React.MouseEvent, nodeId: string) => void
}

export function SurveyInsightsSection({ nodeId, onNodeClick }: SurveyInsightsSectionProps) {
  const [data, setData] = useState<SurveyResponsesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [isPrinting, setIsPrinting] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('print')
    const handler = (e: MediaQueryListEvent) => setIsPrinting(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    void (async () => {
      setLoading(true)
      const result = await SurveyService.getResponses(nodeId)
      setLoading(false)
      if (result.data) setData(result.data)
    })()
  }, [nodeId])

  const reconciledQuestions = useMemo(
    () => reconcileQuestions(data?.questions ?? [], data?.responses ?? []),
    [data]
  )

  if (loading) {
    return (
      <div style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Spin size="small" />
        <Text
          style={{ fontSize: TYPOGRAPHY.caption.fontSize, color: 'var(--color-text-secondary)' }}
        >
          Loading survey data…
        </Text>
      </div>
    )
  }

  const responseCount = data?.responses.length ?? 0
  if (responseCount === 0) return null

  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 12px',
        background: 'var(--color-bg-secondary, #fafafa)',
        borderRadius: 6,
        border: '1px solid var(--color-border, #e8e8e8)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 10,
          cursor: onNodeClick ? 'pointer' : 'default',
        }}
        onClick={
          onNodeClick
            ? (e) => {
                e.stopPropagation()
                onNodeClick(e, nodeId)
              }
            : undefined
        }
      >
        <ClipboardList size={14} color="#6366f1" />
        <Text strong style={{ color: '#6366f1' }}>
          {data?.parentLabel ?? 'Survey'}
        </Text>
        <Text
          style={{
            fontSize: TYPOGRAPHY.caption.fontSize,
            color: 'var(--color-text-secondary)',
            marginLeft: 4,
          }}
        >
          <Users size={11} style={{ verticalAlign: 'middle', marginRight: 2 }} />
          {responseCount} response{responseCount !== 1 ? 's' : ''}
        </Text>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {reconciledQuestions.map((q, i) => (
          <QuestionInsightCard
            key={q.id ?? i}
            question={q}
            questionIndex={i}
            answers={q.answers}
            isPrinting={isPrinting}
            isDeleted={q.isDeleted}
          />
        ))}
      </div>
    </div>
  )
}
